import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Fade,
  Avatar,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Agriculture,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, error, clearError, isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (!authLoading && isAuthenticated) {
    if (user?.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user?.role === 'aic_officer') {
      return <Navigate to="/admin/dashboard" replace />; // AIC Officers use admin dashboard
    } else if (user?.role === 'agent') {
      return <Navigate to="/agent/dashboard" replace />; // Agents have their own dashboard
    } else if (user?.role === 'vendor') {
      return <Navigate to="/vendor/dashboard" replace />; // Vendors have their own dashboard
    } else if (user?.role === 'farmer') {
      return <Navigate to="/farmer/dashboard" replace />;
    } else {
      return <Navigate to="/farmer/dashboard" replace />;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearError();

    if (!email || !password) {
      return;
    }

    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Redirect based on user role
        if (result.user.role === 'farmer') {
          navigate('/farmer/dashboard');
        } else if (result.user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (result.user.role === 'aic_officer') {
          navigate('/admin/dashboard'); // AIC Officers use admin dashboard
        } else if (result.user.role === 'agent') {
          navigate('/agent/dashboard'); // Agents have their own dashboard
        } else if (result.user.role === 'vendor') {
          navigate('/vendor/dashboard'); // Vendors have their own dashboard
        } else {
          navigate('/farmer/dashboard');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demoEmail, demoPassword) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    
    // Small delay to show the credentials being filled
    setTimeout(async () => {
      setLoading(true);
      clearError();
      
      try {
        const result = await login(demoEmail, demoPassword);
        
        if (result.success) {
          if (result.user.role === 'farmer') {
            navigate('/farmer/dashboard');
          } else if (result.user.role === 'admin') {
            navigate('/admin/dashboard');
          } else if (result.user.role === 'aic_officer') {
            navigate('/admin/dashboard'); // AIC Officers use admin dashboard
          } else if (result.user.role === 'agent') {
            navigate('/agent/dashboard'); // Agents have their own dashboard
          } else if (result.user.role === 'vendor') {
            navigate('/vendor/dashboard'); // Vendors have their own dashboard
          } else {
            navigate('/farmer/dashboard');
          }
        }
      } catch (err) {
        console.error('Demo login error:', err);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Fade in timeout={1000}>
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                }}
              >
                <Agriculture sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography component="h1" variant="h4" gutterBottom color="primary" fontWeight="bold">
                Piscicare
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Shrimp Insurance Management System
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Secure access to your farming dashboard
              </Typography>
            </Box>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </Alert>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                },
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Quick Demo Access
            </Typography>
          </Divider>

          {/* Demo Credentials Cards */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2, color: 'primary.main' }}>
              🚀 Available Test Accounts
            </Typography>
            
            {/* First Row - Farmers */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
              <Card 
                sx={{ 
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    👨‍🌾 KARTIKESWAR MOHANTY
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    kartikeswarmohanty1@piscicare.com
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Password: farmer123
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDemoLogin('kartikeswarmohanty1@piscicare.com', 'farmer123')}
                    disabled={loading}
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' },
                      textTransform: 'none',
                    }}
                  >
                    Login as Farmer
                  </Button>
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #8BC34A 0%, #689F38 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    👨‍🌾 PARTHA SARATHI PATRA
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    parthasarathipatra2@piscicare.com
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Password: farmer123
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDemoLogin('parthasarathipatra2@piscicare.com', 'farmer123')}
                    disabled={loading}
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' },
                      textTransform: 'none',
                    }}
                  >
                    Login as Farmer
                  </Button>
                </CardContent>
              </Card>
            </Box>

            {/* Second Row - Admin & Officers */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
              <Card 
                sx={{ 
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    👨‍💼 Admin
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    admin@piscicare.com
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Password: admin123
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDemoLogin('admin@piscicare.com', 'admin123')}
                    disabled={loading}
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' },
                      textTransform: 'none',
                    }}
                  >
                    Login as Admin
                  </Button>
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    🏛️ AIC Officer
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    officer@piscicare.com
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Password: officer123
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDemoLogin('officer@piscicare.com', 'officer123')}
                    disabled={loading}
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' },
                      textTransform: 'none',
                    }}
                  >
                    Login as Officer
                  </Button>
                </CardContent>
              </Card>

              <Card 
                sx={{ 
                  minWidth: 200,
                  background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    👨‍💼 Agent
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    agent@piscicare.com
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Password: agent123
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDemoLogin('agent@piscicare.com', 'agent123')}
                    disabled={loading}
                    sx={{ 
                      background: 'rgba(255,255,255,0.2)',
                      '&:hover': { background: 'rgba(255,255,255,0.3)' },
                      textTransform: 'none',
                    }}
                  >
                    Login as Agent
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;
